import { supabase } from '../lib/supabase';
import { runAudit } from '../rules';
import { pricingService } from './pricingService';
import { recomputeAuditFromSnapshot, compareAuditResults } from './diffEngine';
import { groupAffectedAuditsByUser } from './notificationBatchService';
import { notificationSenderService } from './notificationSenderService';
import {
  StoredAudit,
  ReauditDiff,
  AuditDiff,
  UserNotificationBatch,
  AuditInput,
  ToolInput,
  PricingChange,
} from '../types';
import { SUPPORTED_TOOLS } from '../data/pricing';

export const reauditService = {
  /**
   * Fetches an audit by its internal ID from the Supabase database.
   */
  async getAuditById(id: string): Promise<StoredAudit | null> {
    const { data, error } = await supabase
      .from('audits')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !data) {
      console.error('Error fetching audit by ID:', error);
      return null;
    }

    return {
      id: data.id.toString(),
      publicId: data.public_id,
      createdAt: data.created_at,
      teamSize: data.team_size,
      primaryUseCase: data.primary_use_case,
      toolsJson: data.tools_json || [],
      recommendationsJson: data.recommendations_json || [],
      monthlySavings: data.monthly_savings || 0,
      annualSavings: data.annual_savings || 0,
      aiSummary: data.ai_summary,
      totalMonthlySpend: data.total_monthly_spend || 0,
      efficiencyMetrics: data.efficiency_metrics || {},
      pricingSnapshotId: data.pricing_snapshot_id,
      pricingSnapshotJson: data.pricing_snapshot_json,
    };
  },

  /**
   * Fetches the complete stored audit by its public ID.
   */
  async getStoredAuditByPublicId(publicId: string): Promise<StoredAudit | null> {
    const { data, error } = await supabase
      .from('audits')
      .select('*')
      .eq('public_id', publicId)
      .single();

    if (error || !data) {
      console.error('Error fetching stored audit by public ID:', error);
      return null;
    }

    return {
      id: data.id.toString(),
      publicId: data.public_id,
      createdAt: data.created_at,
      teamSize: data.team_size,
      primaryUseCase: data.primary_use_case,
      toolsJson: data.tools_json || [],
      recommendationsJson: data.recommendations_json || [],
      monthlySavings: data.monthly_savings || 0,
      annualSavings: data.annual_savings || 0,
      aiSummary: data.ai_summary,
      totalMonthlySpend: data.total_monthly_spend || 0,
      efficiencyMetrics: data.efficiency_metrics || {},
      pricingSnapshotId: data.pricing_snapshot_id,
      pricingSnapshotJson: data.pricing_snapshot_json,
    };
  },

  /**
   * Re-evaluates a stored audit using the current pricing data, calculating the diff.
   * Kept for backward compatibility with Phase 2. Phase 3 uses diffEngine directly.
   */
  async computeReauditDiff(storedAudit: StoredAudit): Promise<ReauditDiff> {
    const originalMonthlySpend = storedAudit.totalMonthlySpend;
    const originalPotentialSavings = storedAudit.monthlySavings;
    const originalRecommendations = storedAudit.recommendationsJson;

    const updatedTools: ToolInput[] = storedAudit.toolsJson.map(toolInput => {
      const currentTool = SUPPORTED_TOOLS.find(t => t.id === toolInput.toolId);
      const plan = currentTool?.plans.find(p => p.id === toolInput.tier);

      let monthlySpend = toolInput.monthlySpend;
      if (plan && plan.pricingModel === 'subscription' && plan.monthlyPrice !== null) {
        monthlySpend = plan.monthlyPrice * toolInput.userCount;
      }

      return { ...toolInput, monthlySpend };
    });

    const auditInput: AuditInput = {
      teamSize: storedAudit.teamSize,
      useCase: storedAudit.primaryUseCase,
      tools: updatedTools,
    };
    const newResult = runAudit(auditInput);

    let pricingChanges: PricingChange[] = [];
    if (storedAudit.pricingSnapshotJson) {
      const currentSnapshot = pricingService.serializePricingSnapshot();
      pricingChanges = pricingService
        .computePricingChanges(storedAudit.pricingSnapshotJson, currentSnapshot)
        .filter(change => storedAudit.toolsJson.some(t => t.toolId === change.toolId));
    }

    const spendChanged = originalMonthlySpend !== newResult.totalMonthlySpend;
    const savingsChanged = originalPotentialSavings !== newResult.potentialSavings;
    const recsCountChanged = originalRecommendations.length !== newResult.recommendations.length;
    const changesDetected =
      spendChanged || savingsChanged || recsCountChanged || pricingChanges.length > 0;

    return {
      auditId: storedAudit.id,
      publicId: storedAudit.publicId,
      createdAt: storedAudit.createdAt,
      originalMonthlySpend,
      newMonthlySpend: newResult.totalMonthlySpend,
      originalPotentialSavings,
      newPotentialSavings: newResult.potentialSavings,
      originalRecommendations,
      newRecommendations: newResult.recommendations,
      changesDetected,
      pricingChanges,
    };
  },

  /**
   * Phase 3: Full detect-changes pipeline.
   *
   * Fetches all stored audits, reruns each through the current audit engine,
   * classifies staleness, groups by user, and returns structured results.
   * Safe to call in dry-run mode (no DB writes).
   */
  async detectChanges(options: { dryRun?: boolean } = {}): Promise<{
    auditsChecked: number;
    changesDetected: number;
    staleDiffs: AuditDiff[];
    userBatches: UserNotificationBatch[];
  }> {
    console.log('[detectChanges] Starting with options:', options);

    // 1. Fetch all audits that have a stored pricing snapshot (join leads for email)
    const { data: rows, error } = await supabase
      .from('audits')
      .select('*, leads(email)')
      .not('pricing_snapshot_json', 'is', null);

    if (error) {
      console.error('[detectChanges] Failed to fetch audits:', error);
      return { auditsChecked: 0, changesDetected: 0, staleDiffs: [], userBatches: [] };
    }

    const audits: StoredAudit[] = (rows ?? []).map(row => ({
      id: row.id.toString(),
      publicId: row.public_id,
      createdAt: row.created_at,
      teamSize: row.team_size,
      primaryUseCase: row.primary_use_case,
      toolsJson: row.tools_json || [],
      recommendationsJson: row.recommendations_json || [],
      monthlySavings: row.monthly_savings || 0,
      annualSavings: row.annual_savings || 0,
      aiSummary: row.ai_summary,
      totalMonthlySpend: row.total_monthly_spend || 0,
      efficiencyMetrics: row.efficiency_metrics || {},
      pricingSnapshotId: row.pricing_snapshot_id,
      pricingSnapshotJson: row.pricing_snapshot_json,
    }));

    // 2. Build auditId → email map from joined leads
    const auditEmailMap: Record<string, string> = {};
    for (const row of rows ?? []) {
      const email = row.leads?.[0]?.email;
      if (email) auditEmailMap[row.id.toString()] = email;
    }

    console.log(`[detectChanges] Checking ${audits.length} audits...`);

    // 3. Recompute each audit and classify staleness
    const staleDiffs: AuditDiff[] = [];

    for (const storedAudit of audits) {
      if (!storedAudit.pricingSnapshotJson) continue;

      const newResult = recomputeAuditFromSnapshot(storedAudit);
      const userEmail = auditEmailMap[storedAudit.id];
      const diff = compareAuditResults(storedAudit, newResult, userEmail);

      console.log(
        `[detectChanges] audit=${storedAudit.id} stale=${diff.staleFlag} reason=${diff.stalenessReason}`,
      );

      if (diff.staleFlag) {
        staleDiffs.push(diff);
      }
    }

    // 4. Group stale diffs by user email
    const userBatches =
      staleDiffs.length > 0 ? groupAffectedAuditsByUser(staleDiffs, auditEmailMap) : [];

    // 5. Send emails to affected users if not a dry-run
    if (!options.dryRun && userBatches.length > 0) {
      console.log(`[detectChanges] Triggering emails for ${userBatches.length} user batches...`);
      await notificationSenderService.sendAuditChangeEmails(userBatches);
    }

    console.log(
      `[detectChanges] Done. checked=${audits.length} stale=${staleDiffs.length} userBatches=${userBatches.length}`,
    );

    return {
      auditsChecked: audits.length,
      changesDetected: staleDiffs.length,
      staleDiffs,
      userBatches,
    };
  },
};
